import { useQuery } from '@tanstack/react-query';
import type { IndicatorSnapshot } from '@t212/shared';
import { api } from '@/lib/api';
import { fmtNumber } from '@/lib/format';
import { cn } from '@/lib/cn';

type Props = { ticker: string };

function rsiColor(rsi: number): string {
  if (rsi < 30) return 'text-rose-400';
  if (rsi > 70) return 'text-amber-400';
  return 'text-zinc-300';
}

function row(label: string, value: string, valueClass = 'text-zinc-100 num') {
  return (
    <div className="flex items-center justify-between text-xs py-1.5 border-b border-zinc-900 last:border-b-0">
      <span className="text-zinc-400">{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}

export function IndicatorPanel({ ticker }: Props) {
  const q = useQuery({
    queryKey: ['indicator', ticker],
    queryFn: () => api.indicator(ticker),
  });

  const d: IndicatorSnapshot | undefined = q.data;

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
      <h3 className="text-sm font-medium text-zinc-200 mb-2">Indicators</h3>
      {!d ? (
        <div className="text-xs text-zinc-400 py-6 text-center">Loading…</div>
      ) : (
        <div>
          {d.rsi14 !== undefined &&
            row('RSI (14)', fmtNumber(d.rsi14, 1), cn('num', rsiColor(d.rsi14)))}
          {d.macd &&
            row(
              'MACD hist',
              fmtNumber(d.macd.histogram, 3),
              cn('num', d.macd.histogram >= 0 ? 'text-emerald-400' : 'text-rose-400'),
            )}
          {d.bollinger &&
            row('BB mid', fmtNumber(d.bollinger.middle, 2))}
          {d.atr14 !== undefined && row('ATR (14)', fmtNumber(d.atr14, 3))}
          {d.ema20 !== undefined && row('EMA 20', fmtNumber(d.ema20, 2))}
          {d.ema50 !== undefined && row('EMA 50', fmtNumber(d.ema50, 2))}
          {d.ema200 !== undefined && row('EMA 200', fmtNumber(d.ema200, 2))}
        </div>
      )}
    </div>
  );
}
